import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Typography,
  Box,
  Button,
  IconButton,
  Modal,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import { Plus, X, HelpCircle } from "lucide-react";
import ProductSection from "../ProductSection";
import {
  useFetchUnitsForSelectionQuery,
  useCreateBaseUnitMutation,
} from "../../../../../services/baseQuery";
import {
  errorToast,
  successToast,
} from "../../../../../services/toast.service";
import { CompositeUnitForm } from "../UnitComponents/CompositeUnitForm";
import { TertiaryUnitForm } from "../UnitComponents/TertiaryUnitForm";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  maxHeight: "90vh",
  overflow: "auto",
};

const UnitConfiguration = ({ productData, updateProductData, errors }) => {
  // Fetch units from API using the new selection endpoint
  const {
    data: unitsData,
    isLoading: isUnitsLoading,
    isError: isUnitsError,
    refetch: refetchUnits,
  } = useFetchUnitsForSelectionQuery();

  // Create unit mutation
  const [createBaseUnit, { isLoading: isCreatingUnit }] =
    useCreateBaseUnitMutation();

  // State for unit modal
  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [unitTabValue, setUnitTabValue] = useState(0);
  const [newBaseUnit, setNewBaseUnit] = useState({
    name: "",
    abbreviation: "",
    description: "",
  });

  // Get units from the response
  const baseUnits = unitsData?.data?.baseUnits || [];
  const compositeUnits = unitsData?.data?.compositeUnits || [];
  const tertiaryUnits = unitsData?.data?.tertiaryUnits || [];

  const handleChange = (field, value) => {
    updateProductData({
      units: {
        ...productData.units,
        [field]: value,
      },
    });
  };

  const handleUnitTabChange = (event, newValue) => {
    setUnitTabValue(newValue);
  };

  const handleCreateBaseUnit = async () => {
    try {
      if (!newBaseUnit.name || !newBaseUnit.abbreviation) {
        errorToast("Unit name and abbreviation are required");
        return;
      }

      const response = await createBaseUnit({
        name: newBaseUnit.name,
        abbreviation: newBaseUnit.abbreviation,
        description: newBaseUnit.description,
      }).unwrap();

      if (response.success) {
        successToast("Unit created successfully");

        // Refetch units to update the list
        await refetchUnits();

        // Set the newly created unit as the selected unit
        if (response.data && response.data.unit) {
          handleChange("primaryUnit", response.data.unit._id);
        }

        // Reset form and close modal
        setNewBaseUnit({
          name: "",
          abbreviation: "",
          description: "",
        });
        setUnitModalOpen(false);
      } else {
        errorToast(response.message || "Failed to create unit");
      }
    } catch (error) {
      console.error("Error creating unit:", error);
      errorToast(error.data?.error?.message || "Failed to create unit");
    }
  };

  // Helper function to render unit options with proper display
  const renderUnitOptions = units => {
    return units.map(unit => (
      <MenuItem key={unit._id} value={unit._id}>
        {unit.displayName || `${unit.name} (${unit.abbreviation})`}
      </MenuItem>
    ));
  };

  return (
    <div className="space-y-6">
      <ProductSection data="Unit Configuration" />

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                <Typography
                  variant="subtitle2"
                  className="font-outfit font-medium">
                  Primary Unit
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setUnitModalOpen(true)}
                  sx={{
                    color: "#5652B0",
                    backgroundColor: "#F9F8FE",
                    "&:hover": { backgroundColor: "#EEEDF7" },
                  }}>
                  <Plus size={16} />
                </IconButton>
              </div>

              <FormControl fullWidth size="small" error={!!errors.primaryUnit}>
                {isUnitsLoading ? (
                  <TextField
                    label="Primary Unit"
                    disabled
                    InputProps={{
                      endAdornment: (
                        <CircularProgress size={20} color="inherit" />
                      ),
                      sx: {
                        borderRadius: "0.5rem",
                        fontFamily: "Outfit",
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        fontFamily: "Outfit",
                      },
                    }}
                  />
                ) : (
                  <>
                    <InputLabel
                      id="primary-unit-label"
                      sx={{ fontFamily: "Outfit" }}>
                      Primary Unit
                    </InputLabel>
                    <Select
                      labelId="primary-unit-label"
                      value={productData.units.primaryUnit}
                      onChange={e =>
                        handleChange("primaryUnit", e.target.value)
                      }
                      label="Primary Unit"
                      sx={{
                        borderRadius: "0.5rem",
                        fontFamily: "Outfit",
                      }}>
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {renderUnitOptions(baseUnits)}
                    </Select>
                  </>
                )}
              </FormControl>

              <Typography variant="caption" className="text-gray-500">
                The primary unit is the default unit used for selling this
                product.
              </Typography>
            </div>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className="flex flex-col space-y-4">
              <Typography
                variant="subtitle2"
                className="font-outfit font-medium">
                Secondary Unit (Optional)
              </Typography>

              <FormControl fullWidth size="small">
                {isUnitsLoading ? (
                  <TextField
                    label="Secondary Unit"
                    disabled
                    InputProps={{
                      endAdornment: (
                        <CircularProgress size={20} color="inherit" />
                      ),
                      sx: {
                        borderRadius: "0.5rem",
                        fontFamily: "Outfit",
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        fontFamily: "Outfit",
                      },
                    }}
                  />
                ) : (
                  <>
                    <InputLabel
                      id="secondary-unit-label"
                      sx={{ fontFamily: "Outfit" }}>
                      Secondary Unit
                    </InputLabel>
                    <Select
                      labelId="secondary-unit-label"
                      value={productData.units.secondaryUnit}
                      onChange={e =>
                        handleChange("secondaryUnit", e.target.value)
                      }
                      label="Secondary Unit"
                      sx={{
                        borderRadius: "0.5rem",
                        fontFamily: "Outfit",
                      }}>
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {renderUnitOptions(compositeUnits)}
                    </Select>
                  </>
                )}
              </FormControl>

              <Typography variant="caption" className="text-gray-500">
                An alternative unit that can be used for selling this product.
              </Typography>
            </div>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className="flex flex-col space-y-4">
              <Typography
                variant="subtitle2"
                className="font-outfit font-medium">
                Tertiary Unit (Optional)
              </Typography>

              <FormControl fullWidth size="small">
                {isUnitsLoading ? (
                  <TextField
                    label="Tertiary Unit"
                    disabled
                    InputProps={{
                      endAdornment: (
                        <CircularProgress size={20} color="inherit" />
                      ),
                      sx: {
                        borderRadius: "0.5rem",
                        fontFamily: "Outfit",
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        fontFamily: "Outfit",
                      },
                    }}
                  />
                ) : (
                  <>
                    <InputLabel
                      id="tertiary-unit-label"
                      sx={{ fontFamily: "Outfit" }}>
                      Tertiary Unit
                    </InputLabel>
                    <Select
                      labelId="tertiary-unit-label"
                      value={productData.units.tertiaryUnit}
                      onChange={e =>
                        handleChange("tertiaryUnit", e.target.value)
                      }
                      label="Tertiary Unit"
                      sx={{
                        borderRadius: "0.5rem",
                        fontFamily: "Outfit",
                      }}>
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {renderUnitOptions(tertiaryUnits)}
                    </Select>
                  </>
                )}
              </FormControl>

              <Typography variant="caption" className="text-gray-500">
                Another alternative unit that can be used for selling this
                product.
              </Typography>
            </div>
          </Grid>
        </Grid>

        {/* Help Box */}
        <Box className="mt-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start gap-3">
            <HelpCircle size={20} className="text-blue-500 mt-0.5" />
            <div>
              <Typography
                variant="subtitle2"
                className="font-outfit font-medium text-blue-700">
                About Units
              </Typography>
              <Typography variant="body2" className="text-blue-600 mt-1">
                Units define how your product is measured and sold. The primary
                unit is required and will be the default unit for this product.
                Secondary and tertiary units are optional and provide
                alternative ways to sell the same product.
              </Typography>
            </div>
          </div>
        </Box>
      </div>

      {/* Add Unit Modal */}
      <Modal
        open={unitModalOpen}
        onClose={() => setUnitModalOpen(false)}
        aria-labelledby="add-unit-modal">
        <Box sx={modalStyle}>
          <div className="border-b border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                Add New Unit
              </h2>
              <IconButton size="small" onClick={() => setUnitModalOpen(false)}>
                <X size={18} />
              </IconButton>
            </div>
          </div>

          <div className="p-6">
            <Tabs
              value={unitTabValue}
              onChange={handleUnitTabChange}
              variant="fullWidth"
              sx={{
                mb: 3,
                "& .MuiTab-root": {
                  fontFamily: "Outfit",
                  fontSize: "0.875rem",
                  textTransform: "none",
                  minHeight: "40px",
                },
                "& .Mui-selected": {
                  color: "#5652B0",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#5652B0",
                },
              }}>
              <Tab label="Base Unit" />
              <Tab label="Composite Unit" />
              <Tab label="Tertiary Unit" />
            </Tabs>

            {unitTabValue === 0 && (
              <div className="space-y-4">
                <TextField
                  label="Unit Name"
                  value={newBaseUnit.name}
                  onChange={e =>
                    setNewBaseUnit({ ...newBaseUnit, name: e.target.value })
                  }
                  fullWidth
                  size="small"
                  required
                  placeholder="e.g., Piece, Kilogram, Meter"
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />

                <TextField
                  label="Abbreviation"
                  value={newBaseUnit.abbreviation}
                  onChange={e =>
                    setNewBaseUnit({
                      ...newBaseUnit,
                      abbreviation: e.target.value,
                    })
                  }
                  fullWidth
                  size="small"
                  required
                  placeholder="e.g., PCS, KG, M"
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />

                <TextField
                  label="Description (Optional)"
                  value={newBaseUnit.description}
                  onChange={e =>
                    setNewBaseUnit({
                      ...newBaseUnit,
                      description: e.target.value,
                    })
                  }
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder="Add any additional details about this unit"
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setUnitModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                    disabled={isCreatingUnit}>
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateBaseUnit}
                    disabled={
                      !newBaseUnit.name ||
                      !newBaseUnit.abbreviation ||
                      isCreatingUnit
                    }
                    className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                    {isCreatingUnit ? (
                      <>
                        <CircularProgress size={16} color="inherit" />
                        Creating...
                      </>
                    ) : (
                      "Create Base Unit"
                    )}
                  </button>
                </div>
              </div>
            )}

            {unitTabValue === 1 && (
              <CompositeUnitForm
                onClose={() => {
                  setUnitModalOpen(false);
                  refetchUnits();
                }}
              />
            )}

            {unitTabValue === 2 && (
              <TertiaryUnitForm
                onClose={() => {
                  setUnitModalOpen(false);
                  refetchUnits();
                }}
              />
            )}
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default UnitConfiguration;
